import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Plus, Loader2, KeyRound, ArrowRight } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { AppHeader } from "../components/elysium/AppHeader";
import { Reveal } from "../components/elysium/primitives";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabel = {
  living: "Living",
  deceased_unverified: "In memoriam (unverified)",
  deceased_verified: "In memoriam",
};

export const SoulKeys = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/soulkeys`);
      setData(res.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setData({ owned: [], shared: [] });
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-ely-bg"><Loader2 className="animate-spin text-ely-primary" /></div>;
  if (user === false) return <Navigate to="/login" replace />;

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    setError("");
    try {
      await axios.post(`${API}/soulkeys`, { name: name.trim() });
      setName("");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-ely-bg">
      <div className="grain" aria-hidden="true" />
      <AppHeader />
      <main className="mx-auto max-w-5xl px-6 py-14 md:px-10 md:py-20" data-testid="soulkeys-page">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-ely-primary">Your Soul-Keys</span>
          <h1 className="mt-5 font-serif text-4xl font-light leading-none tracking-tight text-ely-text md:text-6xl">
            The legacies in your care.
          </h1>
        </Reveal>

        <Reveal delay={0.1}>
          <form onSubmit={create} className="mt-10 rounded-xl border border-ely-border/60 bg-ely-surface/50 p-6 md:p-8" data-testid="create-soulkey-form">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ely-muted">Create a new Soul-Key</label>
            <div className="mt-3 flex flex-col gap-3 sm:flex-row">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Grandma Ruth's Story"
                data-testid="soulkey-name-input"
                className="flex-1 rounded-md border border-ely-border bg-ely-bg/60 px-4 py-3 font-sans text-base text-ely-text placeholder:text-ely-muted/60 outline-none focus:border-ely-primary"
              />
              <button
                type="submit"
                disabled={creating || !name.trim()}
                data-testid="create-soulkey-btn"
                className="flex items-center justify-center gap-2 rounded-full bg-ely-primary px-6 py-3 font-sans text-sm font-medium text-ely-bg transition-all hover:bg-ely-primaryHover disabled:opacity-40"
              >
                {creating ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Start a Legacy
              </button>
            </div>
            {error && <p className="mt-3 font-sans text-sm text-red-400" data-testid="soulkeys-error">{error}</p>}
          </form>
        </Reveal>

        {!data ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-ely-primary" /></div>
        ) : (
          <>
            <Section title="You created" empty="Nothing yet — create your first Soul-Key above.">
              {data.owned.map((sk, i) => (
                <SoulKeyItem key={sk.id} sk={sk} badge="Owner" delay={i * 0.05} />
              ))}
            </Section>
            <Section title="Shared with you" empty="No Soul-Keys have been shared with you yet.">
              {data.shared.map((sk, i) => (
                <SoulKeyItem key={sk.id} sk={sk} badge={sk.my_mode} delay={i * 0.05} />
              ))}
            </Section>
          </>
        )}
      </main>
    </div>
  );
};

const Section = ({ title, empty, children }) => {
  const arr = React.Children.toArray(children);
  return (
    <div className="mt-14">
      <h2 className="font-serif text-2xl font-light text-ely-text">{title}</h2>
      <div className="mt-5 space-y-3">
        {arr.length ? arr : <p className="font-sans text-sm font-light text-ely-muted">{empty}</p>}
      </div>
    </div>
  );
};

const SoulKeyItem = ({ sk, badge, delay }) => (
  <Reveal delay={delay}>
    <Link to={`/soulkey/${sk.id}`} data-testid={`soulkey-item-${sk.id}`}>
      <motion.div
        whileHover={{ x: 4 }}
        className="group flex items-center justify-between rounded-xl border border-ely-border/60 bg-ely-surface/40 p-5 transition-colors hover:border-ely-primary/50"
      >
        <div className="flex items-center gap-4">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-ely-border text-ely-primary">
            <KeyRound size={16} />
          </span>
          <div>
            <p className="font-serif text-xl font-light text-ely-text">{sk.name}</p>
            <p className="font-mono text-[10px] uppercase tracking-wider text-ely-muted">
              {statusLabel[sk.status] || sk.status}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full border border-ely-border px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ely-primary">{badge}</span>
          <ArrowRight size={16} className="text-ely-muted transition-transform group-hover:translate-x-1" />
        </div>
      </motion.div>
    </Link>
  </Reveal>
);
